import * as Icons from "@nova/component-icons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useCallback, useMemo, useState } from "react"
import { ImageProcessingState, JobStatusType, SidePanelLayer } from "@nova/constants"
import { get } from "nodash"
import { useUploadFlowContext } from "panels/content/side-panel/upload-flow/useUploadFlowContext"
import { InspectorPanelMetadata } from "features/map/inspector-panel/FlightPanelMetadata"
import {
    ProgressBar,
    UploadProgressBar,
} from "panels/content/side-panel/upload-flow/components/UploadProgressBar"
import { ButtonPrimary } from "@nova/component-btn"
import { DeleteFlight } from "panels/content/side-panel/upload-flow/components/DeleteFlight"
import { trimMiddle } from "@nova/util-strings"
import { getSummaryFromUploads } from "panels/content/side-panel/upload-flow/module"
import { Layer } from "@nova/protobuf"
import { updateVideoColorFinderJob } from "features/pubsub/entityActions/jobs/videoColorFinder"
import { openColorFinder } from "plugins/ColorFinder/V2/redux/slice"

export const VideoColorfinderPreviewFilesDemo = () => {
    const { uploads, job, flightId, selectFlightById } = useUploadFlowContext()
    const dispatch = useAppDispatch()

    const flight = useAppSelector(selectFlightById(flightId)) as Layer
    const summary = getSummaryFromUploads(uploads)

    const [demoProcessingProgress, setDemoProcessingProgress] = useState(0)
    const [demoJobCreatedAt, setDemoJobCreatedAt] = useState<number>()
    const [demoUploadingProgress, setDemoUploadingProgress] = useState(0)

    const startMockedUploading = useCallback(async () => {
        // Simulate uploading for 5 seconds.
        await new Promise<void>((resolve) => {
            const interval = setInterval(() => {
                setDemoUploadingProgress((prev) => {
                    const progress = Math.min(1, prev + 0.01)
                    if (progress >= 1) {
                        clearInterval(interval)
                        resolve()
                    }
                    return progress
                })
            }, 50)
        })

        // Set job status to COMPLETED.
        dispatch(
            updateVideoColorFinderJob({
                id: flightId,
                updates: { status: JobStatusType.COMPLETED },
                context: { flightId },
            })
        )
    }, [dispatch, flightId])

    const startMockedProcessing = useCallback(async () => {
        setDemoJobCreatedAt(new Date().getTime())

        // Set job status to QUEUED.
        dispatch(
            updateVideoColorFinderJob({
                id: flightId,
                updates: { status: JobStatusType.QUEUED },
                context: { flightId },
            })
        )

        // Simulate progress for 5 seconds.
        await new Promise<void>((resolve) => {
            const interval = setInterval(() => {
                setDemoProcessingProgress((prev) => {
                    const progress = Math.min(1, prev + 0.01)
                    if (progress >= 1) {
                        clearInterval(interval)
                        resolve()
                    }
                    return progress
                })
            }, 50)
        })

        startMockedUploading()
    }, [flightId, dispatch, startMockedUploading])

    const [videoFile, srtFile] = useMemo(() => {
        return [
            uploads.find((upload) => {
                const isSRT = get(upload, "isSRT", false)
                return !isSRT
            }),
            uploads.find((upload) => {
                const isSRT = get(upload, "isSRT", false)
                return isSRT
            }),
        ]
    }, [uploads])

    const videoName = get(videoFile, "name") as string
    const srtName = get(srtFile, "name") as string

    const hasBegunProcessing = demoProcessingProgress > 0
    const hasFinishedProcessing = demoProcessingProgress >= 1

    const isUploading = hasFinishedProcessing && demoUploadingProgress < 1
    const hasFinishedUploading = job?.status === JobStatusType.COMPLETED

    return (
        <>
            <div className="border-b border-gray-700" />

            {!hasFinishedUploading ? (
                <>
                    {/* File List (pre-process stage) */}
                    {!hasBegunProcessing && !hasFinishedProcessing && (
                        <div className="my-2 flex flex-col gap-2">
                            <div className="flex justify-between">
                                <h5 className="text-gray-0">Ready to Upload</h5>
                                <h5 className="text-gray-100">{`${uploads.length} files`}</h5>
                            </div>
                            <div className="my-2 flex flex-col gap-1">
                                <FileItem name={videoName || "Video File Missing"} />
                                <FileItem name={srtName || "SRT File Missing"} />
                            </div>
                        </div>
                    )}

                    {/* Processing/Uploading Progress */}
                    {!!demoJobCreatedAt && (
                        <>
                            {/* Processing Progress Bar */}
                            {hasBegunProcessing && !hasFinishedProcessing && (
                                <ProgressBar
                                    progress={demoProcessingProgress}
                                    total={1}
                                    timeElapsed={demoJobCreatedAt}
                                />
                            )}

                            {/* Uploading Progress Bar */}
                            {hasFinishedProcessing && isUploading && (
                                <UploadProgressBar
                                    progress={demoUploadingProgress}
                                    total={1}
                                    timeElapsed={demoJobCreatedAt}
                                />
                            )}
                        </>
                    )}
                </>
            ) : (
                <>
                    {/* "Upload Complete" Message */}
                    {hasFinishedUploading && (
                        <div className="flex items-center gap-1 my-4 select-none">
                            <Icons.Icon Icon={Icons.Checkmark} className="text-green-500" />
                            <h5 className="text-gray-0">Upload Complete</h5>
                        </div>
                    )}
                </>
            )}

            {/* Metadata */}
            <InspectorPanelMetadata
                entity={flight as SidePanelLayer}
                completedUploadCount={summary.stateCount[ImageProcessingState.UploadDone] || 0}
                hideCreator
                hideFileCount
                className="mt-2 border-b border-gray-700"
                isSingleFile
            />

            {/* View Color Data Button */}
            {hasFinishedProcessing && (
                <ButtonPrimary
                    title="View Color Data"
                    RightIcon={Icons.ArrowRight}
                    className="w-full mt-2"
                    onClick={() => {
                        dispatch(openColorFinder({ flightId }))
                    }}
                />
            )}

            {/* Delete & Process Buttons */}
            <div className="flex gap-2 items-center mt-2">
                <DeleteFlight />

                {/* Process Button */}
                {!hasBegunProcessing && !hasFinishedUploading && (
                    <ButtonPrimary
                        title="Process"
                        onClick={startMockedProcessing}
                        className="w-full"
                    />
                )}
            </div>
        </>
    )
}

const FileItem = ({ name }: { name: string }) => {
    return (
        <div className="flex gap-2 items-center justify-between">
            <div className="flex gap-2 items-center">
                <Icons.SmallIcon Icon={Icons.Checkmark} className="text-green-500" />
                <small className="text-gray-100 normalLineHeight">
                    {trimMiddle(name, { maxLength: 25 })}
                </small>
            </div>
        </div>
    )
}
